import { Link } from 'react-router-dom';
import { FileText, Shield, Users, ChevronRight, Check, HelpCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vets-intake-demo/artifacts/e4mtecfo_Untitled%20design%20%2892%29.png";

export const ServicesPage = () => {
  const services = [
    {
      id: 'claims',
      icon: FileText,
      title: 'VA Disability Claims Assistance',
      subtitle: 'New Claims, Increases & Appeals',
      description: 'Navigating the VA disability claims process can feel overwhelming. We provide clear, step-by-step guidance to help you file new claims, request rating increases, or navigate the appeals process.',
      whoItHelps: [
        'Veterans filing their first disability claim',
        'Veterans seeking to increase their current rating',
        'Veterans whose claims have been denied and need to appeal',
        'Veterans unsure where to start with the VA process'
      ],
      whyItMatters: 'Many veterans receive ratings that do not reflect their true conditions simply because they did not know how to present their case effectively. We help ensure you have the best chance of receiving the rating you deserve.',
      features: [
        'Guidance on gathering required evidence',
        'Help understanding VA rating criteria',
        'Support throughout the claims process',
        'Appeals assistance if your claim is denied'
      ]
    },
    {
      id: 'dbq',
      icon: Shield,
      title: 'DBQ & Nexus Letter Guidance',
      subtitle: 'Medical Documentation Support',
      description: 'Disability Benefits Questionnaires (DBQs) and nexus letters are often the key to a successful claim. We help you understand what these documents are, why they matter, and how to obtain them.',
      whoItHelps: [
        'Veterans who need medical evidence for their claims',
        'Veterans whose claims were denied due to insufficient medical evidence',
        'Veterans unsure what a DBQ or nexus letter is',
        'Veterans who need help connecting their condition to their service'
      ],
      whyItMatters: 'A well-documented DBQ or a strong nexus letter can be the difference between a denied claim and an approved one. Many veterans do not realize the importance of these documents until it is too late.',
      features: [
        'Explanation of DBQ requirements',
        'Guidance on obtaining nexus letters',
        'Help understanding what the VA looks for',
        'Support connecting conditions to service'
      ]
    },
    {
      id: 'support',
      icon: Users,
      title: 'VA Process Education & Support',
      subtitle: 'Understanding the System',
      description: 'The VA system can be confusing and frustrating. We provide clear explanations and ongoing support to help you understand what is happening with your claim and what to expect at each stage.',
      whoItHelps: [
        'Veterans who feel lost in the VA process',
        'Veterans who want to understand their options',
        'Family members helping veterans with their claims',
        'Veterans who have questions about VA procedures'
      ],
      whyItMatters: 'Understanding the process reduces stress and helps you make informed decisions about your claim. When you know what to expect, you can better prepare and advocate for yourself.',
      features: [
        'Plain-language explanations of VA procedures',
        'Timeline expectations and status updates',
        'Answers to your questions about the process',
        'Ongoing support throughout your claim journey'
      ]
    }
  ];

  const faqs = [
    {
      question: 'How much do your services cost?',
      answer: 'Contact us to discuss your situation. We believe every veteran deserves support, and we work to make our services accessible.'
    },
    {
      question: 'Are you accredited by the VA?',
      answer: 'We provide guidance and support services. For legal representation or accredited claims agent services, we can help connect you with appropriate resources.'
    },
    {
      question: 'How long does the claims process take?',
      answer: 'VA claims can take anywhere from a few months to over a year. We help you prepare the strongest possible case to avoid delays and denials.'
    },
    {
      question: 'Can you guarantee approval?',
      answer: 'No one can guarantee approval—anyone who does is not being honest. What we can do is help you present the strongest possible case with proper documentation and evidence.'
    }
  ];

  return (
    <div className="page-transition" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-wv-blue py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-wv-red text-lg font-semibold mb-4 tracking-wide uppercase">
              Our Services
            </p>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
              How We Help Veterans
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              We offer comprehensive support for veterans navigating the VA disability 
              claims process. Our services are designed to make your journey clearer 
              and less stressful.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-20 ${index % 2 === 0 ? 'bg-paper-white' : 'bg-white'}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Left Column - Main Info */}
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-wv-red rounded-lg mb-6">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-4">
                  {service.title}
                </h2>
                <p className="text-xl text-wv-red font-semibold mb-6">
                  {service.subtitle}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  {service.description}
                </p>
                
                {/* Features List */}
                <div className="bg-wv-blue/5 rounded-lg p-6 mb-8">
                  <h4 className="font-heading text-xl font-bold text-wv-blue mb-4">
                    What Is Included
                  </h4>
                  <ul className="space-y-3">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-lg">
                        <Check className="w-6 h-6 text-wv-red flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/intake"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid={`service-cta-${service.id}`}
                >
                  Get Started with {service.title.split(' ')[0]} Support
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>

              {/* Right Column - Who It Helps & Why It Matters */}
              <div className="space-y-8">
                <div className="bg-white rounded-lg p-8 border-2 border-gray-100">
                  <h3 className="font-heading text-2xl font-bold text-wv-blue mb-4">
                    Who This Helps
                  </h3>
                  <ul className="space-y-3">
                    {service.whoItHelps.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-lg">
                        <div className="w-2 h-2 bg-wv-red rounded-full mt-2.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-wv-red/10 rounded-lg p-8 border-2 border-wv-red/20">
                  <h3 className="font-heading text-2xl font-bold text-wv-blue mb-4">
                    Why It Matters
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {service.whyItMatters}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* FAQ Section */}
      <section className="py-20 bg-paper-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <HelpCircle className="w-12 h-12 text-wv-red mx-auto mb-4" />
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-8 border-2 border-gray-100"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-heading text-xl font-bold text-wv-blue mb-3">
                  {faq.question}
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-wv-blue" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Complete our simple intake form and tell us about your situation. 
            We will review your information and reach out to discuss how we can help.
          </p>
          <Link
            to="/intake"
            className="btn-primary inline-flex items-center justify-center gap-2"
            data-testid="services-main-cta"
          >
            Start Your Intake Form
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};
